import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { hubAccount, hubBillingPortal, apiChangePassword, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check } from "lucide-react";

export default function Account() {
  const { data, isLoading } = useQuery({ queryKey: ["hub-account"], queryFn: hubAccount });
  const { logout } = useAuth();
  const [msg, setMsg] = useState("");
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdErr, setPwdErr] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  const openPortal = async () => {
    setMsg("");
    try {
      const r = await hubBillingPortal();
      if (r.url) window.location.href = r.url;
    } catch (e) {
      setMsg(formatApiErrorDetail(e?.response?.data?.detail) || "Billing portal unavailable.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdErr("");
    setPwdMsg("");
    if (pwd.next.length < 8) {
      setPwdErr("New password must be at least 8 characters.");
      return;
    }
    setPwdBusy(true);
    try {
      const r = await apiChangePassword({ current_password: pwd.current, new_password: pwd.next });
      if (r.token) localStorage.setItem("cv_token", r.token);
      setPwd({ current: "", next: "" });
      setPwdMsg("Password updated.");
    } catch (e2) {
      setPwdErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not update password.");
    } finally {
      setPwdBusy(false);
    }
  };

  if (isLoading) return <p className="cv-body text-[#9A938A]">Loading...</p>;

  return (
    <div data-testid="hub-account">
      <p className="cv-kicker">Account</p>
      <h1 className="cv-display text-3xl mt-4">Profile and billing.</h1>

      <div className="mt-8 border border-[rgba(28,27,25,0.14)] rounded-[14px] p-6 space-y-4 max-w-lg">
        <Row label="Name" value={data?.name || "Not set"} />
        <Row label="Email" value={data?.email} />
        <Row label="Role" value={data?.role} />
        <Row label="Membership" value={data?.membership?.tier ? `The Table (${data.membership.status})` : "None"} />
      </div>

      <div className="mt-8 max-w-lg">
        <p className="cv-kicker">Billing</p>
        <p className="cv-body text-sm text-[#1F1B16]/70 mt-3">
          Manage payment methods and invoices in the Stripe customer portal.
        </p>
        <button data-testid="account-billing-portal" onClick={openPortal} className="cv-btn-secondary mt-4">
          Open billing portal
        </button>
        {msg && <p data-testid="account-billing-msg" className="cv-body text-sm text-[#1F1B16]/70 border-l-2 border-[#9A938A] pl-3 mt-3">{msg}</p>}
      </div>

      <div className="mt-10 max-w-lg">
        <p className="cv-kicker">Security</p>
        <p className="cv-body text-sm text-[#1F1B16]/70 mt-3">Change your password.</p>
        <form data-testid="account-password-form" onSubmit={changePassword} className="mt-4 space-y-3">
          <input data-testid="account-current-password" type="password" className="cv-input" placeholder="Current password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} autoComplete="current-password" />
          <input data-testid="account-new-password" type="password" className="cv-input" placeholder="New password (8+ characters)" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} autoComplete="new-password" />
          {pwdErr && <p data-testid="account-password-error" className="cv-body text-sm text-[#1F1B16] border-l-2 border-[#A65A43] pl-3">{pwdErr}</p>}
          {pwdMsg && <p data-testid="account-password-success" className="cv-body text-sm text-[#515E4F] border-l-2 border-[#515E4F] pl-3 inline-flex items-center gap-2"><Check size={14} /> {pwdMsg}</p>}
          <button data-testid="account-change-password" type="submit" disabled={pwdBusy} className="cv-btn-secondary disabled:opacity-60">{pwdBusy ? "..." : "Update password"}</button>
        </form>
      </div>

      <div className="mt-10">
        <button data-testid="account-signout" onClick={logout} className="cv-link">Sign out</button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="cv-sans uppercase tracking-[0.12em] text-[0.68rem] text-[#1F1B16]/60">{label}</span>
      <span className="cv-body text-sm text-[#1F1B16]">{value}</span>
    </div>
  );
}
